import { awaitJSON, SingleChannelLambdaFetch } from "../../utils/fetch-utils";
import { DateRange, typedReactMemo } from "../../utils/types";
import { FullPageSpinner } from "../../Components";
import { overrideDateRange } from "../../utils/test-account-utils";
import { RouteComponentProps } from "@reach/router";
import { useSetError } from "../../redux/modals";
import * as P from "@blisspointmedia/bpm-types/dist/Performance";
import * as R from "ramda";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SingleChannelPage from "../SingleChannelPage";
import useLocation from "../../utils/hooks/useLocation";
import {
  DeletePresetParams as DeletePagePresetParams,
  GetPresetsParams as GetPagePresetsParams,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams,
} from "@blisspointmedia/bpm-types/dist/MetricsPage";
import {
  DeletePresetParams as DeleteTablePresetParams,
  DimensionColumn,
  GetPresetParams as GetTablePresetParams,
  MetricsTablePreset,
  SavePresetParams as SaveTablePresetParams,
} from "@blisspointmedia/bpm-types/dist/MetricsTable";
import {
  getDefaultCompareMetrics,
  getDefaultDeliveryOverviewMetrics,
  getDefaultSparkChartMetrics,
  getDeliveryPageDimensionMap,
  getDeliveryPageMetricColumns,
  GetPageDataParams,
  GetPageDataResponse,
  getSparkChartConfig,
} from "@blisspointmedia/bpm-types/dist/Columns";
import {
  App2Channels,
  COMMERCE,
  getFilterDataProcessor,
} from "@blisspointmedia/bpm-types/dist/ScpChannels";
import {
  getDimensionColMetaData,
  getDataColMetaData,
  getDimensionCell,
} from "./sharedPerformanceUtils";
import { getChannelGlossary } from "./glossary";
import { AdInfoRow, DimensionMap } from "../MetricsTable/metricsTableUtils";
import { useExperimentFlag } from "../../utils/experiments/experiment-utils";

interface SingleChannelFactoryProps extends RouteComponentProps {
  urlSettings: string;
  channel: App2Channels;
}

// TODO remove, same jira as below
const TEMP_COMMERCE_KPI_METADATA = {
  orders: {
    id: "orders",
    name: "Orders",
    kpi: "orders",
    decimals: 0,
    maxCpxValue: 10000,
    cpxName: "CPO",
    revenueHeader: "Avg. Rev.",
  },
  units: {
    id: "units",
    name: "Units",
    kpi: "units",
    decimals: 0,
    maxCpxValue: 10000,
    cpxName: "CPU",
    revenueHeader: "Avg. Rev.",
  },
  new_to_brand_orders: {
    id: "new_to_brand_orders",
    name: "New to Brand Orders",
    kpi: "new_to_brand_orders",
    decimals: 0,
    maxCpxValue: 10000,
    cpxName: "CPNTBO",
    revenueHeader: "Avg. Rev.",
  },
  dpv: {
    id: "dpv",
    name: "DPV",
    kpi: "dpv",
    decimals: 2,
    maxCpxValue: 10000,
    cpxName: "CPDPV",
    revenueHeader: "Avg. Rev.",
  },
  sns: {
    id: "sns",
    name: "Subscribe and Save",
    kpi: "sns",
    decimals: 2,
    maxCpxValue: 10000,
    cpxName: "CPSNS",
    revenueHeader: "Avg. Rev.",
  },
  branded_searches: {
    id: "branded_searches",
    name: "Branded Searches",
    kpi: "branded_searches",
    decimals: 0,
    maxCpxValue: 10000,
    cpxName: "CPBS",
    revenueHeader: "Avg. Rev.",
  },
};

export const SingleChannelFactory = typedReactMemo<React.FC<SingleChannelFactoryProps>>(
  ({ location, urlSettings, channel }) => {
    const [dates, setDates] = useState<DateRange>();
    const [fetchingPagePresets, setFetchingPagePresets] = useState(false);
    const [fetchingTablePresets, setFetchingTablePresets] = useState(false);
    const [filterData, setFilterData] = useState<P.GetFilterOptionsResponse>();
    const [pagePresets, setPagePresets] = useState<MetricsPagePreset[]>();
    const [tablePresets, setTablePresets] = useState<MetricsTablePreset[]>();
    const { company } = useLocation();
    const setError = useSetError();
    const [sparkMetrics, sparkPrettyNames] = useMemo(() => getSparkChartConfig(channel), [channel]);
    const columnMetaDataMap = useMemo(() => getDataColMetaData(channel, company), [
      company,
      channel,
    ]);
    const dimensionColumnMetaDataMap = useMemo(() => getDimensionColMetaData(channel), [channel]);
    const glossary = useMemo(() => getChannelGlossary(channel), [channel]);
    const [adInfoMap, setAdInfoMap] = useState<Record<string, AdInfoRow>>({});

    const shouldEnableMotionReplacement = useExperimentFlag("enableMotionReplacement");

    useEffect(() => {
      if (shouldEnableMotionReplacement && company && channel === "social") {
        (async () => {
          try {
            const res = await SingleChannelLambdaFetch("/getAdInfo", {
              method: "GET",
              params: { company },
            });
            const adInfoRows = await awaitJSON(res);
            const adInfoMap = adInfoRows.reduce((acc, row) => {
              acc[row.ad_name] = row;
              return acc;
            }, {} as Record<string, AdInfoRow>);
            setAdInfoMap(adInfoMap);
          } catch (e) {
            console.warn(e);
            setAdInfoMap({});
          }
        })();
      }
    }, [company, channel, shouldEnableMotionReplacement]);

    const getDimensionCellWithAdInfo = useCallback(
      (dimensionData: DimensionMap, dimensionHeader: DimensionColumn) =>
        getDimensionCell(dimensionData, dimensionHeader, adInfoMap),
      [adInfoMap]
    );

    const getPagePresets = useCallback(() => {
      (async () => {
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<GetPagePresetsParams>(
            "/metrics_page_presets",
            {
              params: {
                company,
                mediatype: channel,
              },
            }
          );
          const parsedPresets = await awaitJSON(res);
          if (!parsedPresets.errorMessage) {
            setPagePresets(parsedPresets as MetricsPagePreset[]);
          }
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to fetch page presets. Error: ${error.message}`,
            reportError: error,
          });
        } finally {
          setFetchingPagePresets(false);
        }
      })();
    }, [company, setError, channel]);

    const getTablePresets = useCallback(() => {
      (async () => {
        try {
          setFetchingTablePresets(true);
          const res = await SingleChannelLambdaFetch<GetPagePresetsParams>(
            "/metrics_table_presets",
            {
              params: {
                company,
                mediatype: channel,
              },
            }
          );
          const parsedPresets = await awaitJSON(res);
          if (!parsedPresets.errorMessage) {
            setTablePresets(parsedPresets as MetricsTablePreset[]);
          }
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to fetch table presets. Error: ${error.message}`,
            reportError: error,
          });
        } finally {
          setFetchingTablePresets(false);
        }
      })();
    }, [company, setError, channel]);

    const getTablePreset = useCallback(
      params => {
        return (async () => {
          try {
            const res = await SingleChannelLambdaFetch<GetTablePresetParams>(
              "/metrics_table_preset",
              {
                params,
              }
            );
            return await awaitJSON<MetricsTablePreset>(res);
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Failed to fetch table preset. Error: ${error.message}`,
              reportError: error,
            });
          }
        })();
      },
      [setError]
    );

    const savePagePreset = useCallback(
      body => {
        return (async () => {
          let presetResponse;
          try {
            setFetchingPagePresets(true);
            const res = await SingleChannelLambdaFetch<SavePagePresetParams>(
              "/metrics_page_preset",
              {
                method: "POST",
                body,
              }
            );
            presetResponse = await awaitJSON<P.SavePresetResponse>(res);
            return presetResponse;
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Failed to save page preset. Error: ${error.message}`,
              reportError: error,
            });
            presetResponse = { message: `Failed to save page preset. Error: ${error.message}` };
          } finally {
            getPagePresets();
          }
        })();
      },
      [getPagePresets, setError]
    );

    const saveTablePreset = useCallback(
      body => {
        return (async () => {
          let presetResponse;
          try {
            setFetchingTablePresets(true);
            const res = await SingleChannelLambdaFetch<SaveTablePresetParams>(
              "/metrics_table_preset",
              {
                method: "POST",
                body,
              }
            );
            presetResponse = await awaitJSON<P.SavePresetResponse>(res);
            return presetResponse;
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Failed to save table preset. Error: ${error.message}`,
              reportError: error,
            });
          } finally {
            getTablePresets();
          }
        })();
      },
      [getTablePresets, setError]
    );

    const deletePagePreset = useCallback(
      params => {
        return (async () => {
          try {
            setFetchingPagePresets(true);
            const res = await SingleChannelLambdaFetch<DeletePagePresetParams>(
              "/metrics_page_preset",
              {
                method: "DELETE",
                params,
              }
            );
            await awaitJSON(res);
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Failed to delete page preset. Error: ${error.message}`,
              reportError: error,
            });
          } finally {
            getPagePresets();
          }
        })();
      },

      [getPagePresets, setError]
    );

    const deleteTablePreset = useCallback(
      params => {
        return (async () => {
          try {
            setFetchingTablePresets(true);
            const res = await SingleChannelLambdaFetch<DeleteTablePresetParams>(
              "/metrics_table_preset",
              {
                method: "DELETE",
                params,
              }
            );
            await awaitJSON(res);
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Failed to delete table preset. Error: ${error.message}`,
              reportError: error,
            });
          } finally {
            getTablePresets();
          }
        })();
      },
      [getTablePresets, setError]
    );

    const getDeliveryData = useCallback(params => {
      return (async () => {
        const res = await SingleChannelLambdaFetch("/getChannelDelivery", {
          params: {
            ...params,
            filterState: params.filterState ? JSON.stringify(params.filterState) : undefined,
          },
        });
        return awaitJSON(res);
      })();
    }, []);

    const getPerformanceData = useCallback(
      params => {
        return (async () => {
          const dateRangeToUse = overrideDateRange(company, params.dates);
          const res = await await SingleChannelLambdaFetch<GetPageDataParams>(
            "/getChannelMetricsTableData",
            {
              params: {
                ...params,
                ...(dateRangeToUse || {}),
                channel: channel,
              },
            }
          );
          return await awaitJSON<GetPageDataResponse>(res);
        })();
      },
      [company, channel]
    );

    useEffect(() => {
      if (company && !pagePresets) {
        getPagePresets();
      }
    }, [company, getPagePresets, pagePresets, setError, urlSettings]);

    useEffect(() => {
      if (company && !tablePresets) {
        getTablePresets();
      }
    }, [company, getTablePresets, pagePresets, setError, tablePresets, urlSettings]);

    useEffect(() => {
      if (company && !filterData) {
        (async () => {
          try {
            const res = await SingleChannelLambdaFetch("/getChannelFilterOptions", {
              params: {
                company,
                channel: channel,
                pageID: channel,
              } as any,
            });
            const options = await awaitJSON<P.GetFilterOptionsResponse>(res);
            setFilterData(options);
          } catch (e) {
            const error = e as Error;
            setError({
              message: error.message,
              reportError: error,
            });
          }
        })();
      }
    }, [company, filterData, setError, channel]);
    return pagePresets && tablePresets ? (
      <SingleChannelPage
        columnMetaDataMap={columnMetaDataMap}
        dataFetchKey={header => `${header.kpi}_${header.lag}`}
        dates={dates as any}
        // TODO remove https://tinuiti.atlassian.net/browse/DEV-18417
        defaultKPI={channel === COMMERCE ? "orders" : undefined}
        defaultSparkChartMetrics={getDefaultSparkChartMetrics(channel)}
        defaultCompareMetrics={getDefaultCompareMetrics(channel)}
        defaultDeliveryOverviewMetrics={getDefaultDeliveryOverviewMetrics(channel)}
        deletePagePreset={deletePagePreset}
        deleteTablePreset={deleteTablePreset}
        deliveryDimensionMap={getDeliveryPageDimensionMap(channel).reduce((acc, dim) => {
          acc[`${dim.prettyName}`] = dim.name;
          return acc;
        }, {})}
        deliveryMetricOptions={getDeliveryPageMetricColumns(channel).map(col => ({
          label: col.prettyName,
          isSpend: col.name === "spend",
          value: col.name,
        }))}
        dimensionColumnMetaDataMap={dimensionColumnMetaDataMap}
        fetchingPagePresets={fetchingPagePresets}
        fetchingTablePresets={fetchingTablePresets}
        filterData={R.defaultTo([], filterData)}
        filterDataProcessor={getFilterDataProcessor(channel)}
        getDeliveryData={getDeliveryData}
        getDimensionCell={shouldEnableMotionReplacement ? getDimensionCellWithAdInfo : getDimensionCell}
        getPerformanceData={getPerformanceData}
        getTablePreset={getTablePreset}
        glossary={glossary}
        // TODO remove same jira as above
        kpiMetaData={channel === COMMERCE ? TEMP_COMMERCE_KPI_METADATA : {}}
        location={location}
        pagePresets={R.defaultTo([], pagePresets)}
        prefix={channel}
        savePagePreset={savePagePreset}
        saveTablePreset={saveTablePreset}
        setDates={setDates as any}
        setPagePresets={setPagePresets}
        setTablePresets={setTablePresets}
        sparkChartMetricsMap={sparkMetrics}
        sparkChartMetricPrettyNameMap={sparkPrettyNames}
        tablePresets={R.defaultTo([], tablePresets)}
        urlSettings={urlSettings}
        // TODO remove same jira as above
        useCrossChannelKpis={channel !== COMMERCE}
      />
    ) : (
      <FullPageSpinner />
    );
  }
);
